/**
 * 共通ホバーエフェクト関数
 * UIの一貫性とコードの再利用性を向上
 */

import { SHADOWS } from '../styles/constants';

/**
 * カードホバーエフェクト（軽い浮上効果）
 * @param {Event} e - イベントオブジェクト
 * @param {string} transform - カスタム変形（デフォルト: translateY(-2px)）
 * @param {string} shadow - カスタムシャドウ（デフォルト: md）
 */
export const applyCardHover = (e, transform = 'translateY(-2px)', shadow = SHADOWS.md) => {
  e.currentTarget.style.transform = transform;
  e.currentTarget.style.boxShadow = shadow;
};

/**
 * カードホバー離脱エフェクト
 * @param {Event} e - イベントオブジェクト
 * @param {string} originalShadow - 元のシャドウ（デフォルト: base）
 */
export const removeCardHover = (e, originalShadow = SHADOWS.base) => {
  e.currentTarget.style.transform = 'translateY(0)';
  e.currentTarget.style.boxShadow = originalShadow;
};

/**
 * ボタンホバーエフェクト（背景色変更）
 * @param {Event} e - イベントオブジェクト
 * @param {string} backgroundColor - ホバー時の背景色
 * @param {string} textColor - ホバー時のテキスト色
 * @param {string} scale - スケール変更（デフォルト: 1.05）
 */
export const applyButtonHover = (e, backgroundColor, textColor, scale = '1.05') => {
  e.currentTarget.style.backgroundColor = backgroundColor;
  e.currentTarget.style.color = textColor;
  e.currentTarget.style.transform = `scale(${scale})`;
};

/**
 * ボタンホバー離脱エフェクト
 * @param {Event} e - イベントオブジェクト
 * @param {string} originalBackgroundColor - 元の背景色
 * @param {string} originalTextColor - 元のテキスト色
 */
export const removeButtonHover = (e, originalBackgroundColor, originalTextColor) => {
  e.currentTarget.style.backgroundColor = originalBackgroundColor;
  e.currentTarget.style.color = originalTextColor;
  e.currentTarget.style.transform = 'scale(1)';
};

/**
 * 広告アイテムホバーエフェクト（大きめの浮上効果）
 * @param {Event} e - イベントオブジェクト
 */
export const applyAdItemHover = (e) => {
  e.currentTarget.style.transform = 'translateY(-5px)';
  e.currentTarget.style.boxShadow = SHADOWS.md;
};

/**
 * 広告アイテムホバー離脱エフェクト
 * @param {Event} e - イベントオブジェクト
 */
export const removeAdItemHover = (e) => {
  e.currentTarget.style.transform = 'translateY(0)';
  e.currentTarget.style.boxShadow = SHADOWS.base;
};

/**
 * ナビゲーションボタンホバーエフェクト
 * @param {Event} e - イベントオブジェクト
 * @param {string} hoverBackground - ホバー時の背景
 * @param {string} transform - 変形効果
 */
export const applyNavButtonHover = (e, hoverBackground, transform) => {
  e.currentTarget.style.background = hoverBackground;
  e.currentTarget.style.transform = transform;
};

/**
 * ナビゲーションボタンホバー離脱エフェクト
 * @param {Event} e - イベントオブジェクト
 * @param {string} originalBackground - 元の背景
 * @param {string} originalTransform - 元の変形
 */
export const removeNavButtonHover = (e, originalBackground, originalTransform) => {
  e.currentTarget.style.background = originalBackground;
  e.currentTarget.style.transform = originalTransform;
};

/**
 * サムネイルホバーエフェクト（非アクティブのみ）
 * @param {Event} e - イベントオブジェクト
 * @param {boolean} isActive - アクティブ状態
 * @param {string} scale - スケール値
 */
export const applyThumbnailHover = (e, isActive, scale = '1.05') => {
  if (!isActive) {
    e.currentTarget.style.transform = `scale(${scale})`;
  }
};

/**
 * サムネイルホバー離脱エフェクト
 * @param {Event} e - イベントオブジェクト
 * @param {boolean} isActive - アクティブ状態
 */
export const removeThumbnailHover = (e, isActive) => {
  if (!isActive) {
    e.currentTarget.style.transform = 'scale(1)';
  }
};

/**
 * 汎用ホバーエフェクトファクトリー
 * @param {Object} hoverStyles - ホバー時のスタイル
 * @param {Object} defaultStyles - デフォルトスタイル
 */
export const createHoverEffect = (hoverStyles, defaultStyles) => {
  return {
    onMouseEnter: (e) => {
      Object.keys(hoverStyles).forEach(key => {
        e.currentTarget.style[key] = hoverStyles[key];
      });
    },
    onMouseLeave: (e) => {
      Object.keys(defaultStyles).forEach(key => {
        e.currentTarget.style[key] = defaultStyles[key];
      });
    }
  };
};

// デフォルトエクスポート用オブジェクト（ESLint警告修正）
const hoverEffectsUtils = {
  applyCardHover,
  removeCardHover,
  applyButtonHover,
  removeButtonHover,
  applyAdItemHover,
  removeAdItemHover,
  applyNavButtonHover,
  removeNavButtonHover,
  applyThumbnailHover,
  removeThumbnailHover,
  createHoverEffect,
};

export default hoverEffectsUtils;